import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Building, Users, UserCheck, ListChecks, CalendarRange, BarChart3, ChevronLeft, ChevronRight, Briefcase, DollarSign, FileText, Mail, Calendar, Palmtree, Cog, BookOpen, Newspaper, Database, Activity, ChevronDown, Clock, MessageCircle, ClipboardList, UserPlus, FolderOpen, Receipt, FileSearch, QrCode, Tags, Target, FileEdit, Book, Settings, Boxes, CreditCard, Timer } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

// Map module names to the sidebar paths they control
const MODULE_PATH_MAP = {
  'Dashboard': ['/overview'],
  'Employee Management': ['/employees', '/profile', '/team-calendar', '/my-calendar', '/recruitment', '/onboarding'],
  'Attendance': ['/attendance'],
  'Leave Management': ['/leave-management', '/my-leave'],
  'Payroll': ['/payroll', '/my-salary'],
  'Task Management': ['/tasks', '/my-tasks'],
  'Ping': ['/ping'],
  'Company Policies': ['/company-policies'],
  'Newsroom': ['/newsroom', '/news-management'],
  'Expense Management': ['/expense-management', '/my-expenses', '/expense-analytics'],
  'Analytics': ['/performance', '/reports'],
  'Leads': ['/leads'],
  'Letters': ['/letters'],
  'Contract Ledger': ['/contracts'],
  'Meeting Pad': ['/meeting-pad'],
  'Letterhead Templates': ['/office/letterhead'],
  'QR Scan Logs': ['/office/qr-scan-logs'],
  'Billing': ['/office/billing'],
  'Module Management': ['/module-management'],
  'Timesheets': ['/timesheet', '/team-timesheets'],
};

// Build a Set of allowed paths from office modules
const buildAllowedPaths = (officeModules) => {
  const allowed = new Set();
  for (const mod of officeModules) {
    const paths = MODULE_PATH_MAP[mod.name];
    if (paths) paths.forEach(p => allowed.add(p));
  }
  return allowed;
};

// Paths that are always visible (admin config, not feature modules)
const ALWAYS_VISIBLE_PREFIXES = ['/office/', '/admin/', '/audit-trail', '/module-management', '/manage-offices', '/manage-users', '/documentation', '/system-config'];

const isAlwaysVisible = (path) => ALWAYS_VISIBLE_PREFIXES.some(prefix => path.startsWith(prefix));

// Filter nav items based on allowed module paths
const filterNavItems = (items, allowedPaths, officeModules) => {
  const moduleNames = new Set(officeModules.map(m => m.name));
  return items.reduce((acc, item) => {
    if (item.isGroup) {
      const filteredChildren = item.children.filter(child => {
        // Check if child requires a specific module
        if (child.requiresModule && !moduleNames.has(child.requiresModule)) {
          return false;
        }
        return isAlwaysVisible(child.path) || allowedPaths.has(child.path);
      });
      if (filteredChildren.length > 0) {
        acc.push({ ...item, children: filteredChildren });
      }
    } else {
      if (item.requiresModule && !moduleNames.has(item.requiresModule)) {
        return acc;
      }
      if (isAlwaysVisible(item.path) || allowedPaths.has(item.path)) {
        acc.push(item);
      }
    }
    return acc;
  }, []);
};

// Navigation items based on user role and view mode
const getNavItems = (userRole, activeView = null, hasBadges = false, hasMarketingBadge = false, hasCorrespondenceBadge = false) => {
  if (userRole === 'super_admin') {
    return [
      { label: 'Overview', icon: LayoutDashboard, path: '/overview' },
      { label: 'Platform Analytics', icon: BarChart3, path: '/platform-analytics' },
      { label: 'My Tasks', icon: ListChecks, path: '/my-tasks' },
      { label: 'Manage Offices', icon: Building, path: '/manage-offices' },
      { label: 'Manage Users', icon: Users, path: '/manage-users' },
      { label: 'Documentation', icon: Book, path: '/documentation' },
      { label: 'Module Management', icon: Boxes, path: '/module-management' },
      { label: 'Billing', icon: CreditCard, path: '/billing' },
      { 
        label: 'System Config', 
        icon: Cog, 
        path: '/system-config',
        isGroup: true,
        children: [
          { label: 'Data Cleanup', icon: Database, path: '/admin/data-management' },
          { label: 'Audit Trail', icon: Activity, path: '/audit-trail' },
        ]
      },
    ];
  } else if (userRole === 'office_admin') {
    // Office admin viewing as employee shows employee menu
    if (activeView === 'employee') {
      const items = [
        { label: 'Overview', icon: LayoutDashboard, path: '/overview' },
        { label: 'My Profile', icon: UserCheck, path: '/profile' },
        { label: 'Attendance', icon: Clock, path: '/attendance' },
        { label: 'My Tasks', icon: ListChecks, path: '/my-tasks' },
        { label: 'Timesheet', icon: Timer, path: '/timesheet' },
        { label: 'My Calendar', icon: Calendar, path: '/my-calendar' },
        { label: 'My Expenses', icon: Receipt, path: '/my-expenses' },
      ];
      
      // Add Expense Approvals if user has workflow badges
      if (hasBadges) {
        items.push({ label: 'Expense Approvals', icon: FileSearch, path: '/expense-management' });
      }
      
      // Add Leads if user has Marketing Manager badge
      if (hasMarketingBadge) {
        items.push({ label: 'Leads', icon: Target, path: '/leads' });
      }
      
      // Add Letters if user has Correspondence Manager badge
      if (hasCorrespondenceBadge) {
        items.push({ label: 'Letters', icon: Mail, path: '/letters' });
      }
      
      // Meeting Pad is accessible to all users
      items.push({ label: 'Meeting Pad', icon: FileEdit, path: '/meeting-pad' });
      
      items.push(
        { label: 'My Team', icon: CalendarRange, path: '/team-calendar' },
        { label: 'My Salary', icon: DollarSign, path: '/my-salary' },
        { label: 'My Leave', icon: Palmtree, path: '/my-leave' },
        { label: 'Newsroom', icon: Newspaper, path: '/newsroom' },
        { label: 'Company Policies', icon: BookOpen, path: '/company-policies' },
      );
      
      return items;
    }
    
    // Default admin view
    return [
      { label: 'Overview', icon: LayoutDashboard, path: '/overview' },
      { 
        label: 'My Work', 
        icon: ClipboardList, 
        isGroup: true,
        children: [
          { label: 'My Tasks', icon: ListChecks, path: '/my-tasks' },
          { label: 'Timesheet', icon: Timer, path: '/timesheet' },
          { label: 'My Calendar', icon: Calendar, path: '/my-calendar' },
          { label: 'My Expenses', icon: Receipt, path: '/my-expenses' },
        ]
      },
      { 
        label: 'Team', 
        icon: Users, 
        isGroup: true,
        children: [
          { label: 'Employees', icon: UserCheck, path: '/employees' },
          { label: 'My Team', icon: CalendarRange, path: '/team-calendar' },
          { label: 'Team Timesheets', icon: Timer, path: '/team-timesheets' },
          { label: 'Leads', icon: Target, path: '/leads' },
        ]
      },
      { 
        label: 'HR Operations', 
        icon: UserPlus, 
        isGroup: true,
        children: [
          { label: 'Recruitment', icon: Briefcase, path: '/recruitment' },
          { label: 'Onboarding', icon: ListChecks, path: '/onboarding' },
          { label: 'Attendance', icon: Clock, path: '/attendance' },
          { label: 'Leave', icon: Palmtree, path: '/leave-management' },
          { label: 'Performance', icon: BarChart3, path: '/performance' },
        ]
      },
      { 
        label: 'Finance', 
        icon: DollarSign, 
        isGroup: true,
        children: [
          { label: 'Expenses', icon: Receipt, path: '/expense-management' },
          { label: 'Payroll', icon: DollarSign, path: '/payroll' },
        ]
      },
      { 
        label: 'Documentation', 
        icon: FileText, 
        isGroup: true,
        children: [
          { label: 'Letters', icon: Mail, path: '/letters' },
          { label: 'Contracts', icon: FileText, path: '/contracts' },
        ]
      },
      { 
        label: 'Communications', 
        icon: FolderOpen, 
        isGroup: true,
        children: [
          { label: 'Newsroom', icon: Newspaper, path: '/news-management' },
          { label: 'Company Policies', icon: BookOpen, path: '/company-policies' },
        ]
      },
      { 
        label: 'Office Settings', 
        icon: Cog, 
        isGroup: true,
        children: [
          { label: 'Office Configuration', icon: Settings, path: '/office/configuration' },
          { label: 'Letterhead Templates', icon: FileText, path: '/office/letterhead', requiresModule: 'Letterhead Templates' },
          { label: 'Expense Settings', icon: DollarSign, path: '/office/expense-settings' },
          { label: 'Timesheet Categories', icon: Tags, path: '/office/timesheet-categories' },
          { label: 'QR Scan Logs', icon: QrCode, path: '/office/qr-scan-logs' },
          { label: 'Billing', icon: CreditCard, path: '/office/billing' },
        ]
      },
    ];
  } else {
    // Employee view
    const items = [
      { label: 'Overview', icon: LayoutDashboard, path: '/overview' },
      { label: 'My Profile', icon: UserCheck, path: '/profile' },
      { label: 'Attendance', icon: Clock, path: '/attendance' },
      { label: 'My Tasks', icon: ListChecks, path: '/my-tasks' },
      { label: 'Timesheet', icon: Timer, path: '/timesheet' },
      { label: 'My Calendar', icon: Calendar, path: '/my-calendar' },
      { label: 'My Expenses', icon: Receipt, path: '/my-expenses', testId: 'nav-my-expenses' },
    ];
    
    if (hasBadges) {
      items.push({ label: 'Expense Approvals', icon: FileSearch, path: '/expense-management' });
    }
    
    if (hasMarketingBadge) {
      items.push({ label: 'Leads', icon: Target, path: '/leads' });
    }
    
    if (hasCorrespondenceBadge) {
      items.push({ label: 'Letters', icon: Mail, path: '/letters' });
    }
    
    items.push({ label: 'Meeting Pad', icon: FileEdit, path: '/meeting-pad' });
    
    items.push(
      { label: 'My Team', icon: CalendarRange, path: '/team-calendar' },
      { label: 'My Salary', icon: DollarSign, path: '/my-salary' },
      { label: 'My Leave', icon: Palmtree, path: '/my-leave' },
      { label: 'Newsroom', icon: Newspaper, path: '/newsroom' },
      { label: 'Company Policies', icon: BookOpen, path: '/company-policies' },
    );
    
    return items;
  }
};

const Sidebar = ({ collapsed, onToggle }) => {
  const { user: currentUser, activeView, isOfficeAdmin } = useAuth();
  // Derive the effective role by priority (a promoted employee has both
  // 'employee' and 'office_admin' roles; office_admin must win so admin menus show).
  const roles = currentUser?.roles || [];
  const userRole = roles.some(r => r.role === 'super_admin')
    ? 'super_admin'
    : roles.some(r => r.role === 'office_admin')
      ? 'office_admin'
      : roles[0]?.role;
  
  const [expandedGroups, setExpandedGroups] = useState({});
  const [hasBadges, setHasBadges] = useState(false);
  const [officeModules, setOfficeModules] = useState(null); // null = loading, [] = loaded
  
  const hasMarketingBadge = currentUser?.special_badges?.some(b => 
    b.badge_type === 'marketing_manager'
  ) || false;
  
  const hasCorrespondenceBadge = currentUser?.special_badges?.some(b => 
    b.badge_type === 'correspondence_manager'
  ) || false;

  // Check if user has workflow badges (for expense approvals menu)
  useEffect(() => {
    const checkBadges = async () => {
      const token = localStorage.getItem('auth_token');
      const currentOffice = localStorage.getItem('current_office');
      const officeId = currentOffice ? JSON.parse(currentOffice)?.id : null;
      
      if (!token || !currentUser?.id || !officeId) return;
      
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        
        const response = await fetch(`${API_URL}/api/expenses/workflow/my-queue`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Office-ID': officeId,
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setHasBadges(data.badges && data.badges.length > 0);
        }
      } catch (error) {
        console.error('Error checking badges:', error);
      }
    };
    
    checkBadges();
  }, [currentUser?.id]);

  // Fetch office modules for non-super-admin users
  useEffect(() => {
    const fetchOfficeModules = async () => {
      if (userRole === 'super_admin') {
        setOfficeModules(null); // Super admin sees everything, no filtering
        return;
      }

      const token = localStorage.getItem('auth_token');
      const currentOffice = localStorage.getItem('current_office');
      const officeId = currentOffice ? JSON.parse(currentOffice)?.id : null;
      
      if (!token || !officeId) {
        setOfficeModules(null);
        return;
      }
      
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/modules/office/${officeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setOfficeModules(data);
        } else {
          setOfficeModules(null); // On error, show all items
        }
      } catch {
        setOfficeModules(null); // On error, show all items
      }
    };
    
    fetchOfficeModules();
  }, [userRole, currentUser?.id]);

  let navItems = getNavItems(userRole, activeView, hasBadges, hasMarketingBadge, hasCorrespondenceBadge);

  // Apply module-based filtering for non-super-admin users
  if (userRole !== 'super_admin' && officeModules !== null && officeModules.length > 0) {
    const allowedPaths = buildAllowedPaths(officeModules);
    navItems = filterNavItems(navItems, allowedPaths, officeModules);
  }

  const toggleGroup = (label) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <motion.aside
      initial={{ width: collapsed ? 80 : 288 }}
      animate={{ width: collapsed ? 80 : 288 }}
      transition={{ duration: 0.22, ease: [0.2, 0.0, 0, 1] }}
      className="bg-card border-r border-border flex flex-col h-full"
      data-testid="sidebar"
    >
      {/* Header */}
      <div className="p-6 border-b border-border flex items-center justify-between">
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-1.5"
          >
            <img src="/logo192-nobg.png" alt="desknodes" className="w-9 h-9" />
            <span className="text-lg font-bold" style={{ color: '#0F2E6E' }}>
              desknodes
            </span>
          </motion.div>
        )}
        {collapsed && (
          <img src="/logo192-nobg.png" alt="desknodes" className="w-9 h-9 mx-auto" />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1" data-testid="sidebar-nav">
        {navItems.map((item, idx) => (
          item.isGroup ? (
            <div key={item.label} className="space-y-1">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(item.label)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:text-foreground hover:bg-accent`}
                data-testid={`nav-group-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${expandedGroups[item.label] ? 'rotate-180' : ''}`} />
                  </>
                )}
              </button>
              
              {/* Group Children */}
              <AnimatePresence>
                {(expandedGroups[item.label] || collapsed) && !collapsed && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="ml-4 space-y-1 overflow-hidden"
                  >
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isActive
                              ? 'text-white shadow-sm'
                              : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                          }`
                        }
                        style={({ isActive }) => isActive ? { backgroundColor: '#0F2E6E' } : {}}
                        data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <child.icon className="w-4 h-4 flex-shrink-0" />
                        <span>{child.label}</span>
                      </NavLink>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`
              }
              style={({ isActive }) => isActive ? { backgroundColor: '#0F2E6E' } : {}}
              data-testid={item.testId || `nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 + idx * 0.02 }}
                >
                  {item.label}
                </motion.span>
              )}
            </NavLink>
          )
        ))}
      </nav>

      {/* Toggle Button */}
      <div className="p-3 border-t border-border">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-accent transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5 text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Collapse</span>
            </>
          )}
        </button>
      </div>
    </motion.aside>
  );
};

export default Sidebar;
